import * as React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";

export interface OptionType {
  inputValue?: string;
  name: string;
  label?: string;
}

interface CustomMultipleSelectProps {
  options: OptionType[];
  value: OptionType | OptionType[] | null;
  onChange: (value: OptionType | OptionType[] | null) => void;
  multiple?: boolean;
  onInputChange?: (value: string) => void;
}

const CustomMultipleSelect: React.FC<
  CustomMultipleSelectProps &
    Omit<TextFieldProps, "options" | "onChange" | "value">
> = ({ options, value, onChange, multiple = false, onInputChange, ...inputProps }) => {
  return (
    <Autocomplete
      multiple={multiple}
      filterSelectedOptions={multiple}
      value={value as any}
      onChange={(event, newValue) => {
        if (multiple) {
          const processedValue = newValue.map((item: OptionType) => {
            if (typeof item === "string") {
              return { name: item, label: item };
            }
            if (item?.inputValue) {
              return { name: item.inputValue, label: item.inputValue };
            }
            return item;
          });
          onChange(processedValue as OptionType[]);
        } else if (typeof newValue === "string") {
          onChange({ name: newValue, label: newValue });
        } else if (newValue?.inputValue) {
          onChange({ name: newValue.inputValue, label: newValue.inputValue });
        } else {
          onChange(newValue as OptionType);
        }
      }}
      onInputChange={(event, value, reason) => {
        if (reason !== 'reset') {
          onInputChange?.(value)
        }
      }}
      filterOptions={(options) => options}
      selectOnFocus
      handleHomeEndKeys
      autoHighlight
      includeInputInList
      options={options}
      isOptionEqualToValue={(option, selectedValue) =>
        option.name.trim().toLowerCase() === selectedValue.name.trim().toLowerCase()
      }
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        if (option.inputValue) {
          return option.inputValue;
        }
        return option.label || option.name;
      }}
      renderOption={(liProps, option) => {
        const { key, ...rest } = liProps
        return <li key={key} {...rest}>{option.label || option.name}</li>
      }}
      renderTags={(value: readonly OptionType[], getTagProps) =>
        value.map((option: OptionType, index: number) => (
          <Chip
            variant="outlined"
            label={option.label || option.name}
            {...getTagProps({ index })}
          />
        ))
      }
      freeSolo
      fullWidth
      renderInput={(params) => (
        <TextField {...params} {...inputProps} fullWidth />
      )}
    />
  );
};

export default CustomMultipleSelect;
